import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Layers, Loader2, Printer } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Brand } from "../components/Brand";
import { PassportSheet, PAPERS } from "../components/passport/PassportSheet";
import { PassportCard } from "../components/passport/PassportCard";
import { API, PRINT_HEIGHTS, DEFAULT_HEIGHT } from "../lib/api";

const LETTER_REGIONS = ["US", "CA", "MX", "PH", "CL", "CO", "VE", "DO", "GT", "CR", "PA", "PR"];
const detectPaper = (requested) => {
  if (PAPERS[requested]) return requested; // ?paper=rollo|letter|a4 wins (deep links, print automation)
  const saved = localStorage.getItem("duckify_paper");
  if (PAPERS[saved]) return saved;
  const region = (navigator.language || "").split("-")[1] || "";
  return LETTER_REGIONS.includes(region.toUpperCase()) ? "letter" : "a4";
};

export default function PassportPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [duck, setDuck] = useState(null);
  const [shipped, setShipped] = useState(null);
  const [error, setError] = useState(null);
  const [paper, setPaper] = useState(() => detectPaper(params.get("paper")));
  const height = PRINT_HEIGHTS.includes(Number(params.get("height"))) ? Number(params.get("height")) : DEFAULT_HEIGHT;

  useEffect(() => {
    axios
      .get(`${API}/share/${id}`)
      .then((r) => setDuck(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This duck could not be loaded."));
    axios
      .get(`${API}/share/${id}/order-status`)
      .then((r) => setShipped(["shipped", "delivered"].includes(r.data?.status) ? r.data : null))
      .catch(() => setShipped(null));
  }, [id]);

  const pickPaper = (p) => {
    setPaper(p);
    localStorage.setItem("duckify_paper", p);
  };

  return (
    <div className="App min-h-screen bg-dots print:bg-none print:min-h-0">
      <style>{`@page { size: ${PAPERS[paper].css}; margin: 0; }`}</style>
      <header className="h-20 border-b border-border/70 bg-background/80 backdrop-blur-xl print:hidden">
        <div className="max-w-5xl mx-auto h-full px-4 sm:px-8 flex items-center justify-between gap-3">
          <Link to="/" data-testid="passport-logo-link" className="flex items-center gap-3">
            <Brand />
          </Link>
          <Link to={`/s/${id}`} data-testid="passport-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to the duck
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10 print:p-0 print:max-w-none">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4 print:hidden">
          <div>
            <span className="text-xs font-mono2 uppercase tracking-widest text-accent font-semibold">Duck Passport</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-2">
              {PAPERS[paper].labelStock ? "4×6 label, ready to print" : "A6 card, ready to print"}
            </h1>
            <p className="text-muted-foreground text-sm mt-2">
              {PAPERS[paper].labelStock
                ? "Load 4×6″ labels in your Rollo, print at 100% (no “fit to page”) — the card is sized to the label and switches to high-contrast black & white for thermal printing."
                : `Print at 100% (no “fit to page”), then cut along the corner marks. Fits ${PAPERS[paper].label} paper.`}
            </p>
            <Link to={`/labels/${id}?height=${height}`} data-testid="passport-labels-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline mt-3">
              <Layers className="h-4 w-4" /> Print a batch of Rollo labels
            </Link>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div data-testid="paper-toggle" className="flex rounded-full bg-muted/60 p-1">
              {Object.entries(PAPERS).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`paper-${key}`}
                  onClick={() => pickPaper(key)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${paper === key ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button data-testid="print-passport-btn" onClick={() => window.print()} disabled={!duck} className="rounded-full h-11 gap-2 font-bold duck-shadow">
              <Printer className="h-4 w-4" /> Print
            </Button>
          </div>
        </div>

        {error && <div data-testid="passport-error" className="rounded-3xl border border-destructive/40 bg-card p-8 text-center text-destructive">{error}</div>}
        {!duck && !error && (
          <div className="grid place-items-center h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        )}
        {duck && (
          <PassportSheet paper={paper}>
            <PassportCard duck={duck} height={height} shipped={shipped} mono={!!PAPERS[paper].labelStock} />
          </PassportSheet>
        )}
      </main>
    </div>
  );
}
